import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';
import { Calendar, TrendingUp, TrendingDown, Minus, Award, AlertTriangle, BookOpen, Users } from 'lucide-react';

const API_URL = window.location.origin;

const statusColors = {
  satisfatorio: '#10B981',
  atencao: '#F59E0B',
  critico: '#EF4444'
};

export function PainelTrimestre({ year, filtros }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trimestre, setTrimestre] = useState('1º Trimestre');
  const [turmaId, setTurmaId] = useState('');

  useEffect(() => {
    loadData();
  }, [year, trimestre, turmaId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: year.toString(), trimestre });
      if (turmaId) params.append('class_group_id', turmaId);
      
      const res = await fetch(`${API_URL}/api/central-notas/painel-trimestre?${params}`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error('Erro ao carregar painel trimestre:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!data) return null;

  const { kpis, disciplinas, turmas, comparativo, tipos_avaliacao } = data;

  // Dados para o gráfico de pizza de distribuição de notas
  const distribuicaoNotas = [
    { name: 'Satisfatórias', value: kpis.notas_satisfatorias, color: statusColors.satisfatorio },
    { name: 'Atenção', value: kpis.notas_atencao, color: statusColors.atencao },
    { name: 'Críticas', value: kpis.notas_criticas, color: statusColors.critico }
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6" data-testid="painel-trimestre">
      {/* Filtros */}
      <Card className="border-purple-100">
        <CardContent className="pt-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-slate-600">Período:</span>
            </div>
            
            <Select value={trimestre} onValueChange={setTrimestre}>
              <SelectTrigger className="w-[180px] bg-white border-purple-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="1º Trimestre">1º Trimestre</SelectItem>
                <SelectItem value="2º Trimestre">2º Trimestre</SelectItem>
                <SelectItem value="3º Trimestre">3º Trimestre</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={turmaId || "all"} onValueChange={(v) => setTurmaId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[200px] bg-white border-purple-200">
                <SelectValue placeholder="Todas as Turmas" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-[300px]">
                <SelectItem value="all">Todas as Turmas</SelectItem>
                {(filtros?.turmas || []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* KPIs do Trimestre */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="pt-4">
            <div className="text-sm opacity-80">Média Geral</div>
            <div className="text-3xl font-bold">{kpis.media_geral}</div>
            <Badge className={`mt-1 ${kpis.media_geral >= 7 ? 'bg-green-500' : kpis.media_geral >= 5 ? 'bg-yellow-500' : 'bg-red-500'}`}>
              {kpis.media_geral >= 7 ? 'Satisfatório' : kpis.media_geral >= 5 ? 'Atenção' : 'Crítico'}
            </Badge>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-purple-100">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-purple-600">
              <Users className="w-4 h-4" />
              <span className="text-sm">Alunos</span>
            </div>
            <div className="text-2xl font-bold text-slate-800">{kpis.total_alunos}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-purple-100">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-purple-600">
              <BookOpen className="w-4 h-4" />
              <span className="text-sm">Total Notas</span>
            </div>
            <div className="text-2xl font-bold text-slate-800">{kpis.total_notas}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <div className="text-sm text-green-700">Satisfatórias</div>
            <div className="text-2xl font-bold text-green-600">{kpis.notas_satisfatorias}</div>
            <div className="text-xs text-green-600">≥ 7.0</div>
          </CardContent>
        </Card>
        
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="pt-4">
            <div className="text-sm text-yellow-700">Atenção</div>
            <div className="text-2xl font-bold text-yellow-600">{kpis.notas_atencao}</div>
            <div className="text-xs text-yellow-600">5.0 - 6.9</div>
          </CardContent>
        </Card>
        
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-4">
            <div className="text-sm text-red-700">Críticas</div>
            <div className="text-2xl font-bold text-red-600">{kpis.notas_criticas}</div>
            <div className="text-xs text-red-600">&lt; 5.0</div>
          </CardContent>
        </Card>
      </div>

      {/* Comparativo com Trimestre Anterior */}
      {comparativo && (
        <Card className="border-purple-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              {comparativo.tendencia === 'up' ? (
                <TrendingUp className="w-5 h-5 text-green-500" />
              ) : comparativo.tendencia === 'down' ? (
                <TrendingDown className="w-5 h-5 text-red-500" />
              ) : (
                <Minus className="w-5 h-5 text-gray-500" />
              )}
              Comparativo com {comparativo.trimestre_anterior}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div>
                <span className="text-sm text-slate-500">Média anterior:</span>
                <span className="ml-2 font-semibold">{comparativo.media_anterior}</span>
              </div>
              <div>
                <span className="text-sm text-slate-500">Diferença:</span>
                <span className={`ml-2 font-semibold ${
                  comparativo.diferenca > 0 ? 'text-green-600' : 
                  comparativo.diferenca < 0 ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {comparativo.diferenca > 0 ? '+' : ''}{comparativo.diferenca}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance por Disciplina */}
        <Card className="border-purple-100">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-600" />
              Performance por Disciplina
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={disciplinas.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 10]} />
                  <YAxis type="category" dataKey="nome" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    formatter={(value) => [value.toFixed(2), 'Média']}
                    contentStyle={{ backgroundColor: 'white', borderRadius: '8px' }}
                  />
                  <Bar dataKey="media" radius={[0, 4, 4, 0]}>
                    {disciplinas.slice(0, 10).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={statusColors[entry.status]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Distribuição de Notas */}
        <Card className="border-purple-100">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-purple-600" />
              Distribuição de Notas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={distribuicaoNotas}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {distribuicaoNotas.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance por Turma */}
      <Card className="border-purple-100">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            Performance por Turma
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={turmas.slice(0, 15)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nome" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                <YAxis domain={[0, 10]} />
                <Tooltip 
                  formatter={(value, name) => [value.toFixed(2), name === 'media' ? 'Média' : name]}
                  contentStyle={{ backgroundColor: 'white', borderRadius: '8px' }}
                />
                <Bar dataKey="media" radius={[4, 4, 0, 0]}>
                  {turmas.slice(0, 15).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={statusColors[entry.status]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Por Tipo de Avaliação */}
      {tipos_avaliacao && tipos_avaliacao.length > 0 && (
        <Card className="border-purple-100">
          <CardHeader>
            <CardTitle className="text-lg">Média por Tipo de Avaliação</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {tipos_avaliacao.map((tipo, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 truncate">{tipo.tipo}</div>
                  <div className="text-xl font-bold" style={{ color: statusColors[tipo.media >= 7 ? 'satisfatorio' : tipo.media >= 5 ? 'atencao' : 'critico'] }}>
                    {tipo.media}
                  </div>
                  <div className="text-xs text-slate-400">{tipo.total} notas</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
